import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

// استيراد المودالز
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AdjustmentModal from './AdjustmentModal';
import ReorderModal from './ReorderModal';
import SmartPrintButton from './PrintButton'; 

// استيراد السياقات والأدوات الموحدة
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import { 
  ArrowLeft, Save, Activity, RefreshCw, BarChart3, AlertCircle, 
  Paperclip, Tag, DollarSign, Info, ChevronLeft, ChevronRight, Type, Minus, Plus, Printer, History
} from 'lucide-react';

const InventoryDetails = () => {
  const { itemnum, location: locParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

  const { canUpdate } = usePermissions('inventory');
  
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [activeTab, setActiveTab] = useState('BALANCES');
  const [data, setData] = useState(null);
  const [transLog, setTransLog] = useState([]);
  const [loadingTrans, setLoadingTrans] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [count, setCount] = useState(0); 
  const [showAdjModal, setShowAdjModal] = useState(false);    
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const recordId = `${itemnum}-${locParam}`; // للربط مع המودالز

  // --- مكون التحكم في الخط ---
  const FontControls = () => (
    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
        <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
      </div>
      <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
    </div>
  );

  const fetchInventoryDetail = useCallback(async () => {
    if (!itemnum || !locParam) return;
    try {
      const res = await axios.get(`/api/inventory/${itemnum}/${locParam}`);
      setData(res.data);
    } catch (err) {
      toast.error(t('common.load_error', "Inventory record not found"));
    }
  }, [itemnum, locParam, t]);

  useEffect(() => { fetchInventoryDetail(); }, [fetchInventoryDetail]);

  const loadNavigation = useCallback(async () => {
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`/api/inventory/${itemnum}/${locParam}/navigation${location.search}`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { console.error("Nav failed", err); }
  }, [itemnum, locParam, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const goTo = (targetPath) => targetPath && navigate(`/inventory/${targetPath}${location.search}`);

  useEffect(() => {
    if (activeTab === 'TRANSACTIONS' && itemnum && locParam) {
      setLoadingTrans(true);
      axios.get(`/api/inventory/transactions/${itemnum}/${locParam}`)
        .then(res => setTransLog(res.data))
        .catch(() => toast.error(t('common.error', "Error loading transactions")))
        .finally(() => setLoadingTrans(false));
    }
  }, [activeTab, itemnum, locParam, t]);

  const fetchCount = useCallback(async () => {
    if (!recordId) return;
    try {
      const res = await axios.get(`/api/attachments/count/INVENTORY/${recordId}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Attachment count err:", err); }
  }, [recordId]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  useEffect(() => {
    const openStatusModal = () => setShowStatusModal(true);
    const openReorderModal = () => setShowReorderModal(true);
    const handleOpenHistory = () => setShowHistoryModal(true);
    
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    window.addEventListener('GLOBAL_REORDER_HANDLER', openReorderModal);
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    
    return () => {
      window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
      window.removeEventListener('GLOBAL_REORDER_HANDLER', openReorderModal);
      window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    };
  }, []);

  if (!data) return <div className="p-20 text-center animate-pulse font-black text-gray-300 uppercase tracking-widest">{t('common.loading', 'Loading Stock Profile...')}</div>;

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase duration-300 transition-[font-size] tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* --- MODALS --- */}
      <AdjustmentModal isOpen={showAdjModal} onClose={() => setShowAdjModal(false)} itemData={data} onComplete={fetchInventoryDetail} />    
      <ReorderModal isOpen={showReorderModal} onClose={() => setShowReorderModal(false)} data={data} onComplete={fetchInventoryDetail} />
      
      {showStatusModal && (
        <StatusModal currentStatus={data.status} recordId={recordId} appName="INVENTORY" onClose={() => setShowStatusModal(false)} />
      )}
        
      {showHistoryModal && (
        <StatusHistoryModal isOpen={showHistoryModal} appName="INVENTORY" recordId={recordId} onClose={() => setShowHistoryModal(false)} />
      )}

      {isDocModalOpen && (
        <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="INVENTORY" ownerid={recordId} />
      )}
      
      {/* --- 1. TOP TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/inventory${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          
          <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
              <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
            </div>
            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
          </div>
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {canUpdate && (
            <button onClick={() => setShowAdjModal(true)} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[0.9em] font-black shadow-md transition-all">
              <RefreshCw size={14}/> {t('inventory.actions.adjustment', 'Stock Adjustment')}
            </button>
          )}

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          
          <button onClick={() => setShowStatusModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.change_status', 'Change Status')}><Tag size={16}/></button>
          
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.attachments', 'Attachments')}>
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
          </button>
          
          <SmartPrintButton moduleName="INVENTORY" refId={recordId} extraParams={{ location: data.location, report_type: 'GL_SUMMARY' }} />
          
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.status_history', 'Status History')}><History size={16}/></button>
          
          <FontControls />
        </div>
      </div>

      {/* --- 2. HEADER INFO --- */}
      <div className="bg-white border-b px-8 py-4 flex items-center gap-12 shadow-sm z-20">
        <div className="flex flex-col min-w-[200px]">
          <span className="text-[0.9em] text-gray-400 font-black uppercase tracking-widest">{t('inventory.details.profile', 'Inventory Profile')}</span>
          <div className={`flex items-center gap-2 mt-1 `}>
            <span className="text-xl font-black text-[#1a3353]">{data.itemnum}</span>
            <span className="text-gray-400 font-bold italic text-sm">@{data.location}</span>
          </div>
        </div>
        
        <div className={`flex flex-col border-gray-200 px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
          <label className="text-[0.9em] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.status', 'Status')}</label>
          <span className={`mt-1 px-3 py-0.5 rounded-full font-black text-[0.9em] w-fit ${data.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {data.status}
          </span>
        </div>
      </div>

      {/* --- 3. Content Wrapper --- */}
      <div className="flex-1 p-8 grid grid-cols-12 gap-6 overflow-hidden">
        
        {/* Sidebar Info */}
        <div className="col-span-3 space-y-4">
            <div className="bg-white border-t-4 border-blue-900 p-6 shadow-md space-y-6 rounded-sm">
                <h3 className={`font-black text-gray-400 border-b pb-2 text-[0.9em] tracking-widest flex items-center gap-2 `}>
                    <Activity size={14} className="text-blue-600"/> {t('inventory.details.reorder_logic', 'Reorder Logic')}
                </h3>
                <StatField label={t('inventory.details.reorder_pt', 'Reorder Point')} value={data.minlevel || '0.00'} highlight="text-red-600" />
                <StatField label={t('inventory.details.eoq', 'Economic Order Qty')} value={data.orderqty || '0.00'} />
                
                <div className="p-4 bg-red-50 border border-red-100 rounded-sm">
                    <div className={`flex items-center gap-2 text-red-700 font-black text-[0.9em] `}>
                        <AlertCircle size={12}/> {t('inventory.details.critical_lvl', 'Critical Level')}
                    </div>
                    <p className="text-[0.9em] text-red-400 italic mt-1 lowercase leading-relaxed">
                      {t('inventory.details.critical_desc', 'Auto-generate PR when balance hits reorder point.')}
                    </p>
                </div>
            </div>
        </div>

        {/* Main Tabs Container */}
        <div className="col-span-9 flex flex-col bg-white border rounded-sm shadow-sm overflow-hidden">
            <div className="bg-gray-100 border-b flex px-4 shadow-inner">
                <TabBtn active={activeTab === 'BALANCES'} onClick={() => setActiveTab('BALANCES')} label={t('inventory.tabs.balances', 'Balances')} />
                <TabBtn active={activeTab === 'TRANSACTIONS'} onClick={() => setActiveTab('TRANSACTIONS')} label={t('inventory.tabs.trans', 'Transaction History')} />
                <TabBtn active={activeTab === 'COSTS'} onClick={() => setActiveTab('COSTS')} label={t('inventory.tabs.costs', 'Costs')} />
            </div>

            <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
                {activeTab === 'BALANCES' && (
                    <div className="grid grid-cols-2 gap-12 animate-in fade-in duration-300">
                        <div className="space-y-6">
                            <h4 className={`font-black text-blue-900 text-[0.9em] uppercase border-blue-600 ${isRTL ? 'border-r-4 pr-3 text-right' : 'border-l-4 pl-3 text-left'}`}>
                              {t('inventory.details.current_stock', 'Current Stock Level')}
                            </h4>
                            <div className="bg-slate-50 p-8 rounded text-center border shadow-inner">
                                <span className="text-4xl font-black text-slate-800">{parseFloat(data.curbal).toFixed(2)}</span>
                                <p className="text-gray-500 font-bold mt-2 uppercase tracking-widest text-[0.9em]">{t('inventory.details.avail_qty', 'Available Quantity')}</p>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <h4 className={`font-black text-blue-900 text-[0.9em] uppercase border-orange-600 ${isRTL ? 'border-r-4 pr-3 text-right' : 'border-l-4 pl-3 text-left'}`}>
                              {t('inventory.details.reserved_qty', 'Reserved Qty')}
                            </h4>
                            <div className="bg-orange-50 p-8 rounded text-center border border-orange-100 shadow-inner">
                                <span className="text-4xl font-black text-orange-600">{parseFloat(data.reserved || 0).toFixed(2)}</span>
                                <p className="text-orange-400 font-bold mt-2 uppercase tracking-widest text-[0.9em]">{t('inventory.details.held_wo', 'Held for Work Orders')}</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {activeTab === 'TRANSACTIONS' && (
                  <div className="bg-white border rounded shadow-sm overflow-hidden animate-in slide-in-from-bottom duration-300">
                    <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                      <thead className="bg-slate-50 border-b text-[0.9em] font-black text-gray-500 uppercase tracking-widest">
                        <tr>
                          <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.date', 'Date')}</th>
                          <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.type', 'Type')}</th>
                          <th className={`p-3 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('inventory.tables.qty', 'Quantity')}</th>
                          <th className={`p-3 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('inventory.tables.actual_cost', 'Actual Cost')}</th>
                          <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.user', 'User')}</th>
                          <th className="p-3 text-center">{t('inventory.tables.reference', 'Reference')}</th>
                        </tr>
                      </thead>
                      <tbody className="font-bold text-[0.9em]">
                        {loadingTrans ? (
                          <tr><td colSpan="6" className="p-10 text-center animate-pulse text-gray-400 uppercase tracking-widest">{t('common.loading', 'Fetching Audit Trail...')}</td></tr>
                        ) : transLog.length === 0 ? (
                          <tr><td colSpan="6" className="p-20 text-center text-gray-300 font-black uppercase italic tracking-widest">{t('inventory.messages.no_trans', 'No Transaction History Found')}</td></tr>
                        ) : transLog.map((tr, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50 transition-colors">
                            <td className={`p-3 text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>
                                {new Date(tr.transdate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>
                              <span className={`px-2 py-0.5 rounded-full text-[0.8em] font-black ${
                                tr.transtype === 'RECEIPT' ? 'bg-green-100 text-green-700' :
                                tr.transtype === 'ISSUE' ? 'bg-red-100 text-red-700' :
                                tr.transtype === 'TRANSFER' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {t(`inventory.trans_types.${tr.transtype.toLowerCase()}`, tr.transtype)}
                              </span>
                            </td>
                            <td className={`p-3 ${isRTL ? 'border-l text-left' : 'border-r text-right'} ${tr.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {tr.quantity > 0 ? `+${tr.quantity}` : tr.quantity}
                            </td>
                            <td className={`p-3 font-mono text-gray-500 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                {parseFloat(tr.actualcost).toLocaleString()}
                            </td>
                            <td className={`p-3 text-blue-900 uppercase italic text-[9px] ${isRTL ? 'border-l' : 'border-r'}`}>{tr.enterby}</td>
                            <td className="p-3 text-center">
                                {tr.ponum && <span className="text-[9px] bg-slate-100 px-2 rounded border border-slate-200 mx-1">PO: {tr.ponum}</span>}
                                {tr.refwo && <span className="text-[9px] bg-slate-100 px-2 rounded border border-slate-200 mx-1">WO: {tr.refwo}</span>}
                                {tr.transtype === 'TRANSFER' && (
                                    <div className={`text-[8px] text-gray-400 mt-1 flex items-center justify-center gap-1 `}>
                                        {tr.fromstore} <ArrowLeft size={10}/> {tr.tostore}
                                    </div>
                                )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeTab === 'COSTS' && (
                  <div className="animate-in fade-in zoom-in-95 duration-300 space-y-8">
                    {/* Financial Summary Cards */}
                    <div className="grid grid-cols-3 gap-6">
                      <CostStatCard 
                        label={t('inventory.details.avg_cost', 'Average Unit Cost')} 
                        value={data?.avgcost} 
                        icon={<DollarSign size={16} className="text-green-600"/>} 
                        desc={t('inventory.details.avg_cost_desc', 'Weighted average of all receipts')}
                        isRTL={isRTL}
                      />
                      <CostStatCard 
                        label={t('inventory.details.total_val', 'Total Stock Value')} 
                        value={(data?.curbal * data?.avgcost)} 
                        icon={<BarChart3 size={16} className="text-blue-600"/>} 
                        desc={t('inventory.details.total_val_desc', 'Current Balance × Avg Cost')}
                        highlight="text-blue-900"
                        isRTL={isRTL}
                      />
                      <CostStatCard 
                        label={t('inventory.details.last_cost', 'Last Receipt Cost')} 
                        value={data?.lastcost || data?.avgcost} 
                        icon={<RefreshCw size={16} className="text-orange-600"/>} 
                        desc={t('inventory.details.last_cost_desc', 'Cost from the most recent PO')}
                        isRTL={isRTL}
                      />
                    </div>
                
                    {/* Costing Method Info */}
                    <div className={`bg-slate-50 border-slate-400 p-6 rounded-sm ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`}>
                      <div className={`flex items-center gap-3 mb-2 `}>
                        <Info size={18} className="text-slate-500" />
                        <span className="font-black text-slate-700 text-[10px] tracking-widest uppercase">{t('inventory.details.val_method', 'Inventory Valuation Method')}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-bold leading-relaxed lowercase italic">
                        {t('inventory.details.val_desc1', 'This item uses the')} <span className="text-blue-800 font-black">{t('inventory.details.avg_method', 'AVERAGE COSTING')}</span> {t('inventory.details.val_desc2', 'method. the unit cost is recalculated automatically upon every purchase order receipt to reflect market price fluctuations.')}
                      </p>
                    </div>
                  </div>
                )}                
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Stateless Helpers ---
const StatField = ({ label, value, highlight = "text-blue-900" }) => (
    <div className="flex flex-col border-b border-gray-100 pb-2">
        <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest">{label}</label>
        <span className={`text-sm font-black ${highlight}`}>{value}</span>
    </div>
);

const TabBtn = ({ active, onClick, label }) => (
    <button 
        onClick={onClick}
        className={`px-8 py-3.5 font-black tracking-widest transition-all text-[0.9em] uppercase ${active ? 'bg-white text-blue-900 border-t-4 border-t-blue-900 shadow-sm' : 'text-gray-400 hover:bg-gray-200'}`}
    >
        {label}
    </button>
);

const CostStatCard = ({ label, value, icon, desc, highlight = "text-green-700", isRTL }) => (
  <div className={`bg-white border p-6 rounded-sm shadow-sm hover:shadow-md transition-shadow group ${isRTL ? 'text-right' : 'text-left'}`}>
    <div className={`flex justify-between items-start mb-4 `}>
      <div className="p-2 bg-gray-50 rounded-sm group-hover:bg-white transition-colors border border-gray-100 shadow-inner">
        {icon}
      </div>
      <span className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest border border-gray-100 px-2 py-0.5 rounded-full">Financials</span>
    </div>
    <div className="flex flex-col">
      <label className="text-[0.9em] font-black text-gray-400 uppercase mb-1 tracking-widest">{label}</label>
      <span className={`text-2xl font-black ${highlight}`}>
        {parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
        <small className={`text-[0.9em] text-gray-400 ${isRTL ? 'mr-1' : 'ml-1'}`}>USD</small>
      </span>
      <p className="text-[0.8em] text-blue-400 mt-2 font-bold italic uppercase tracking-wider">{desc}</p>
    </div>
  </div>
);

export default InventoryDetails;